/**
 * 밴드 구간 선택을 서버 질의 축으로 바꾼다(순수 계산).
 *
 * 서버는 **판정 어휘를 갖지 않고** 수치 구간만 받는다(진단 spec D1·D6).
 * 그래서 "비슷함/자주 다름/크게 다름" 이라는 이름은 이 모듈에서만 쓴다. 서버에는
 * `band_*_min`·`band_*_max` 숫자만 보낸다. 임계값의 SoT 는 `diagnostics/bandDeviation.ts` 이므로
 * 이 모듈은 수를 다시 정의하지 않는다.
 *
 * **축마다 따로 고른다.** 서버에서 두 축은 독립이고, 함께 적용하면 AND 다. 화면도 축마다
 * 드롭다운을 두어 이 뜻을 그대로 보여 준다. 한 드롭다운에 두 축을 묶으면 조건부 개수가 맞지 않는다.
 * 한 축을 적용한 상태에서 센 반대 축 칸의 수는 "두 축 모두" 의 수다. 그런데 그 칸을 고르면
 * 먼저 적용한 축이 해제되어 결과가 달라진다.
 *
 * **구간은 반열림 `min <= x < max` 다.** 양끝을 모두 닫으면 경계값(0.35 인 에피소드)이
 * 두 구간에 동시에 속한다. 양끝을 모두 열면 어느 구간에도 속하지 않는다. 이 규약은 화면의
 * 심각도 판정(`deviationSeverity` 가 `>= 주의컷`)과도 일치한다.
 */

import type { EpisodeBandAxisFacet, EpisodeListFilters } from "@/api";
import {
  DEVIATION_ALERT_FRACTION,
  DEVIATION_WARN_FRACTION,
  deviationSeverity,
} from "./diagnostics/bandDeviation";

/** 대상 축. 서버 파라미터 접두어와 같은 값이다. */
export type BandFilterAxis = "action" | "observation";

/** 구간 이름. 화면 문구에 쓴다. 서버에는 수치만 보낸다. */
export type BandSeverityBand = "ok" | "warn" | "alert";

export const BAND_FILTER_AXES: readonly BandFilterAxis[] = ["action", "observation"];

/** 드롭다운의 표시 순서. 느슨한 구간부터 나열한다. */
export const BAND_SEVERITY_BANDS: readonly BandSeverityBand[] = ["ok", "warn", "alert"];

/** 구간 하나의 범위 `[하한, 상한)`. 없는 끝은 undefined 다. */
type Interval = { min?: number; max?: number };

const INTERVALS: Record<BandSeverityBand, Interval> = {
  ok: { max: DEVIATION_WARN_FRACTION },
  warn: { min: DEVIATION_WARN_FRACTION, max: DEVIATION_ALERT_FRACTION },
  alert: { min: DEVIATION_ALERT_FRACTION },
};

const AXIS_KEYS = {
  action: ["band_action_min", "band_action_max"],
  observation: ["band_observation_min", "band_observation_max"],
} as const satisfies Record<
  BandFilterAxis,
  readonly [keyof EpisodeListFilters, keyof EpisodeListFilters]
>;

/**
 * 한 축의 구간을 바꾼 사본을 반환한다. null 이면 그 축의 컷을 제거한다.
 * 두 축은 독립이므로 다른 축과 채널 검색은 그대로 둔다.
 */
export function applyBandSeverity(
  filters: EpisodeListFilters,
  axis: BandFilterAxis,
  band: BandSeverityBand | null,
): EpisodeListFilters {
  const [minKey, maxKey] = AXIS_KEYS[axis];
  const rest = { ...filters };
  delete rest[minKey];
  delete rest[maxKey];
  if (band === null) {
    return rest;
  }
  const interval = INTERVALS[band];
  return {
    ...rest,
    ...(interval.min === undefined ? {} : { [minKey]: interval.min }),
    ...(interval.max === undefined ? {} : { [maxKey]: interval.max }),
  };
}

/**
 * 현재 그 축에 적용된 구간. 적용된 구간이 없거나, 구간에 맞지 않는 수치이면 null 이다.
 * 맞지 않는 수치는 MCP 나 다른 세션이 적용한 임의 값이다. 없는 선택지를 만들어 드롭다운이
 * 빈 칸을 그리게 하지 않는다. 맞지 않는 컷이 적용되어 있는지는 `hasBandCut` 이 따로 알려 준다.
 */
export function readBandSeverity(
  filters: EpisodeListFilters,
  axis: BandFilterAxis,
): BandSeverityBand | null {
  if (!hasBandCut(filters, axis)) {
    return null;
  }
  const [minKey, maxKey] = AXIS_KEYS[axis];
  return (
    BAND_SEVERITY_BANDS.find((band) => {
      const interval = INTERVALS[band];
      return filters[minKey] === interval.min && filters[maxKey] === interval.max;
    }) ?? null
  );
}

/** 그 축에 컷이 하나라도 적용되어 있는지 반환한다. */
export function hasBandCut(filters: EpisodeListFilters, axis: BandFilterAxis): boolean {
  const [minKey, maxKey] = AXIS_KEYS[axis];
  return filters[minKey] !== undefined || filters[maxKey] !== undefined;
}

/** 그 축에 적용된 수치 구간. 구간에 맞지 않는 컷을 칩에 수치 그대로 표시할 때 쓴다. */
export function bandCut(
  filters: EpisodeListFilters,
  axis: BandFilterAxis,
): { min: number | undefined; max: number | undefined } {
  const [minKey, maxKey] = AXIS_KEYS[axis];
  return { min: filters[minKey] as number | undefined, max: filters[maxKey] as number | undefined };
}

/**
 * facets 의 고정 폭 칸(0.05 × 20)을 화면 구간 셋으로 합친다.
 *
 * 칸은 **중점**으로 판정한다. 그래서 칸 경계가 부동소수(0.35000000000000003 같은 값)로 와도
 * 칸이 속하는 구간이 바뀌지 않는다. 임계값(0.35·0.45)이 칸 경계와 일치하므로 한 칸이 두 구간에
 * 걸치지 않는다.
 */
export function bandSeverityCounts(axis: EpisodeBandAxisFacet): Record<BandSeverityBand, number> {
  const counts: Record<BandSeverityBand, number> = { ok: 0, warn: 0, alert: 0 };
  for (const bin of axis.bins) {
    counts[deviationSeverity((bin.min + bin.max) / 2)] += bin.count;
  }
  return counts;
}
